import { useCallback } from 'react';

import { useShowStoreFeeItem } from '@givebacks/api/store';
import { ConsumerStudentFee } from '@givebacks/api/student';
import { Button, ButtonProps, Skeleton } from 'ui';
import { useDisclosure } from 'ui/Hooks';

import { useFeeCartHandler } from '../../../hooks/useFeeCartHandler';
import { AddPartialPaymentModal } from '../../modals/AddPartialPaymentModal/AddPartialPaymentModal';

type AddFeeToCartButtonGuardProps = {
  studentFee: ConsumerStudentFee;
  buttonProps?: ButtonProps;
};

interface AddFeeToCartButtonProps extends Omit<AddFeeToCartButtonGuardProps, 'studentFee'> {
  feeId: string;
  causeId: string;
  amountRemaining: number;
  studentFeeId: string;
  allowsPartialPayment: boolean;
  onPartialPayment: () => void;
}

const AddFeeToCartButton = ({
  feeId,
  causeId,
  amountRemaining,
  studentFeeId,
  allowsPartialPayment,
  onPartialPayment,
  buttonProps,
}: AddFeeToCartButtonProps) => {
  const { handleCartUpdate, isLoading } = useFeeCartHandler({
    causeId,
    feeId,
    price: amountRemaining,
    studentFeeId: studentFeeId,
  });

  const handleButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      // Prevent any bubbling of the click event up to the container
      event.preventDefault();
      event.stopPropagation();
      if (allowsPartialPayment) {
        onPartialPayment();
      } else {
        handleCartUpdate();
      }
    },
    [handleCartUpdate, allowsPartialPayment, onPartialPayment]
  );

  return (
    <Button size="xs" loading={isLoading} onClick={handleButtonClick} {...buttonProps}>
      Add to Cart
    </Button>
  );
};

/**
 * Renders a button that will either open the partial payment modal or call useFeeCartHandler to handle
 * adding or updating an existing cart with a `new` fee.
 *
 * This component it'self does not handle updating existing cart items, that is handled from within
 * the `AddPartialPaymentModal` component.
 */
const AddFeeToCartButtonGuard = ({ studentFee, buttonProps }: AddFeeToCartButtonGuardProps) => {
  const [value, handlers] = useDisclosure(false);
  const { data: storeItem, isLoading } = useShowStoreFeeItem({
    feeId: studentFee.fee.uuid,
    causeId: studentFee.cause.uuid,
  });

  if (isLoading) {
    return <Skeleton height={24} width={55} />;
  }

  if (!storeItem) {
    return null;
  }

  return (
    <>
      <AddFeeToCartButton
        feeId={storeItem.uuid}
        causeId={studentFee.cause.uuid}
        amountRemaining={studentFee.amount_remaining}
        studentFeeId={studentFee.uuid}
        allowsPartialPayment={studentFee.fee.allow_partial_payment}
        onPartialPayment={handlers.open}
        buttonProps={buttonProps}
      />
      <AddPartialPaymentModal
        studentFee={studentFee}
        opened={value}
        onClose={handlers.close}
        storeItemId={storeItem.uuid}
      />
    </>
  );
};

export { AddFeeToCartButtonGuard as AddFeeToCartButton };
